import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function SavingsGoals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', target: '', current: '', deadline: '' });

  useEffect(() => { fetchGoals(); }, []);

  const fetchGoals = async () => {
    try {
      const res = await api.get('/goals');
      const data = res.data?.data || res.data || [];
      setGoals(Array.isArray(data) ? data : []);
    } catch (e) { toast.error('Failed to load goals'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.target) return toast.error('Name and target required');
    try {
      await api.post('/goals', form);
      toast.success(editingId ? 'Goal updated!' : 'Goal created!');
      setShowForm(false); setEditingId(null); setForm({ name: '', target: '', current: '', deadline: '' });
      fetchGoals();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try { await api.delete('/goals/' + id); toast.success('Deleted'); fetchGoals(); }
    catch (e) { toast.error('Failed'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Savings Goals</h1>
          <p className="text-gray-500">{goals.length} active goal{goals.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setEditingId(null); setForm({ name: '', target: '', current: '', deadline: '' }); setShowForm(!showForm); }}
          className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
          {showForm ? '✕ Cancel' : '+ New Goal'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Goal' : 'Create New Goal'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input type="text" placeholder="Goal Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            <input type="number" step="0.01" placeholder="Target Amount *" value={form.target} onChange={e => setForm({...form, target: e.target.value})} required
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            <input type="number" step="0.01" placeholder="Current Savings" value={form.current} onChange={e => setForm({...form, current: e.target.value})}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            <input type="date" placeholder="Deadline" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            <button type="submit" className="sm:col-span-2 bg-indigo-600 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-700 font-medium">
              {editingId ? 'Update Goal' : 'Create Goal'}
            </button>
          </form>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="text-5xl mb-4">🎯</div>
          <h3 className="text-lg font-semibold text-gray-900">No Goals Set</h3>
          <p className="text-gray-500 mt-1">Set savings goals to track your financial progress</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals.map(g => {
            const progress = g.target > 0 ? Math.min(((g.current || 0) / g.target) * 100, 100) : 0;
            return (
              <div key={g._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-gray-900 text-lg">{g.name}</h3>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingId(g._id); setForm({ name: g.name, target: g.target, current: g.current || '', deadline: g.deadline || '' }); setShowForm(true); }}
                      className="text-gray-400 hover:text-indigo-600 p-1">✏️</button>
                    <button onClick={() => handleDelete(g._id)} className="text-gray-400 hover:text-red-600 p-1">🗑️</button>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all" style={{ width: progress + '%' }}></div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{Math.round(progress)}%</p>
                <p className="text-sm text-gray-500 mt-1">{formatCurrency(g.current || 0)} of {formatCurrency(g.target)}</p>
                {g.deadline && <p className="text-xs text-gray-400 mt-2">📅 Due: {new Date(g.deadline).toLocaleDateString()}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
